import React from 'react';
import Link from 'next/link';
import classNames from 'classnames';

import { MembershipIllustration } from '@components/membershipIllustration/MembershipIllustration';
import { MembershipPrice } from '@components/membershipPrice/MembershipPrice';
import { SplitHeading } from '@components/splitHeading/SplitHeading';
import { Svg } from '@components/svg/Svg';
import { TextMarquee } from '@components/textMarquee/TextMarquee';

import { MEMBERSHIP_URL } from '@data/constants';
import { SupabaseProductRecordWithPrices } from '@interfaces';

import styles from './zine-membership-banner.module.scss';

interface ZineMembershipBannerProps {
	className?: string;
	product: SupabaseProductRecordWithPrices | null;
	isArchivePostsBanner?: boolean;
}

export const ZineMembershipBanner = ({
	className,
	product,
	isArchivePostsBanner = false,
}: ZineMembershipBannerProps) => {
	if (!product) {
		return null;
	}

	const tierUrl = `${MEMBERSHIP_URL}/${product.slug}`;

	const headingText = isArchivePostsBanner
		? 'Unlock full access\nwith a membership'
		: 'Discover more as an\nElsewhere member';

	const style = {
		'--zine-membership-banner-background-color': product.metadata.color,
	} as React.CSSProperties;

	return (
		<div className={classNames(className, styles.banner)} style={style}>
			<div className={styles.wrapper}>
				{isArchivePostsBanner ? (
					<p className={classNames('font-t5-bold', styles.info)}>
						<Svg
							aria-hidden="true"
							className={styles['lock-icon']}
							id="lock-icon"
						/>
						Archive Posts are only available to{' '}
						<Link className="anchor" href={tierUrl}>
							{product.name}
						</Link>
					</p>
				) : null}
				<SplitHeading
					className={styles.heading}
					heading={headingText}
					tag="h2"
				/>
				<MembershipIllustration
					className={styles.illustration}
					// `max()` is the calculated result of the `fluid-length` function in the scss file
					sizes="max(12rem, 12rem + 12.0187793427vw - 2.8169014085rem)"
					tierLevel={product.metadata.tier_level}
				/>
				<Link
					className={classNames(styles.link, 'button')}
					href="https://join.elsewherebrooklyn.com/freaks-with-benefits"
				>
					Join now for{' '}
					<MembershipPrice includeInterval={false} prices={product.prices} />
				</Link>
			</div>
			<TextMarquee
				className={classNames(styles.marquee, 'font-t5-bold')}
				text="•&emsp;Full Zine archive&emsp;•&emsp;Member-only newsletter&emsp;•&emsp;Digital community access&emsp;•&emsp;+ much more&emsp;"
			/>
		</div>
	);
};
